import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Logo from '../../assets/image/Logo.png';
import Loader from '../../components/Live/Loader';
import ShowPasswordIcon from '../../assets/svg/ShowPassword.svg';
import HidePasswordIcon from '../../assets/svg/HidePassword.svg';
import axios from 'axios';
import { API } from '../../shared/api/Api';
import { useLocation, useNavigate } from 'react-router-dom';
import { showToast } from '../../utils/utils';

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
};

const ResetPassword = () => {
    const [token, setToken] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmationPassword, setConfirmationPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmationPassword, setShowConfirmationPassword] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    // Toggle visibility for password fields
    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
    const toggleConfirmationPasswordVisibility = () => setShowConfirmationPassword((prev) => !prev);

    useEffect(() => {
        const data = params.get('data');
        if (data) {
            try {
                const decodedData = decodeURIComponent(data);
                const parsedData = JSON.parse(decodedData);

                setToken(parsedData.token);
                setEmail(parsedData.email);
                setIsVerified(true);
            } catch (error) {
                console.error('Error decoding or parsing the data parameter:', error);
                showToast('Invalid URL data', 'Error', 'error');
            }
        } else if (location.state?.email) {
            setEmail(location.state.email);
        }
    }, [location.search, location.state]);

    const handleCheckToken = async () => {
        setIsLoading(true);
        try {
            const { data } = await axios.post(
                API.Admin.checkToken,
                { email_or_phone: email, token },
                { headers: { lang: 'en' } }
            );

            if (data.status === 200 && data.errorCode === 0) {
                showToast('Code Verified', data.message, 'success');
                setIsVerified(true);
            } else {
                showToast('Verification Failed', data.message, 'error');
            }
        } catch (error) {
            console.error('Error verifying token:', error);
            showToast('Something went wrong', 'Error', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (password !== confirmationPassword) {
            showToast('Passwords do not match', 'Error', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const { data } = await axios.post(
                API.Admin.resetPassword,
                {
                    email_or_phone: email,
                    token: token,
                    password: password,
                    password_confirmation: confirmationPassword
                },
                { headers: { Accept: "application/json", lang: 'en' } }
            );

            if (data.status === 200 && data.errorCode === 0) {
                showToast('Password Reset Successful', data.message, 'success');
                navigate('/Login');
            } else {
                showToast('Reset Failed', data.message, 'error');
            }
        } catch (error) {
            console.error('Error resetting password:', error);
            showToast('Something went wrong', 'Error', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="mt-[30%] sl:mt-[20%] ll:mt-[15%]">
                <Loader />
            </div>
        );
    }

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="flex flex-1 justify-center items-center mx-auto w-full h-screen p-4 md:p-6 sl:p-8"
        >
            <div className="flex flex-col justify-center items-center mx-auto space-y-4 md:space-y-6 sl:space-y-8 ll:space-y-10">
                <motion.img
                    variants={itemVariants}
                    className="w-24 h-24 md:w-28 md:h-28 sl:w-32 sl:h-32 ll:w-36 ll:h-36"
                    src={Logo}
                    alt="logo"
                />

                <motion.h1
                    variants={itemVariants}
                    className="text-3xl md:text-4xl sl:text-5xl ll:text-6xl font-medium text-primary text-center"
                >
                    {!isVerified ? 'We’ve sent you an email' : 'Great!!'}
                </motion.h1>

                <motion.span
                    variants={itemVariants}
                    className="text-lg md:text-xl sl:text-2xl ll:text-3xl font-medium text-primary text-center"
                >
                    {!isVerified
                        ? 'Check your inbox for the reset password code.'
                        : 'Now you can change your password. Choose it wisely.'}
                </motion.span>

                {!isVerified ? (
                    <motion.div
                        variants={itemVariants}
                        className="flex flex-col justify-center items-center mx-auto space-y-6 md:space-y-8 sl:space-y-10 w-full max-w-xs md:max-w-sm sl:max-w-md ll:max-w-lg"
                    >
                        <motion.input
                            variants={itemVariants}
                            type="text"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            placeholder="Enter Your Code"
                            className="w-full h-input md:h-input-md sl:h-input-lg px-4 md:px-6 sl:px-8 bg-accent rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base md:text-lg sl:text-xl ll:text-2xl placeholder:text-secondary/70"
                            whileFocus={{ scale: 1.02 }}
                        />

                        <motion.button
                            variants={itemVariants}
                            type="button"
                            onClick={handleCheckToken}
                            className="w-full h-input md:h-input-md sl:h-input-lg border-2 border-primary text-primary md:text-lg sl:text-xl ll:text-2xl rounded-lg hover:bg-primary hover:text-white transition-colors font-medium"
                            whileHover="hover"
                            whileTap="tap"
                        >
                            Verify
                        </motion.button>
                    </motion.div>
                ) : (
                    <motion.div
                        variants={itemVariants}
                        className="flex flex-col justify-center items-center mx-auto space-y-6 md:space-y-8 sl:space-y-10 w-full max-w-xs md:max-w-sm sl:max-w-md ll:max-w-lg"
                    >
                        <motion.div
                            variants={itemVariants}
                            className="relative w-full"
                        >
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                className="w-full h-input md:h-input-md sl:h-input-lg px-4 md:px-6 sl:px-8 bg-accent rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base md:text-lg sl:text-xl ll:text-2xl placeholder:text-secondary/70 pr-12 md:pr-14 sl:pr-16"
                            />
                            <motion.button
                                type="button"
                                onClick={togglePasswordVisibility}
                                className="absolute right-4 md:right-6 sl:right-8 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-accent-dark"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                <img
                                    className="w-6 h-6 md:w-7 md:h-7 sl:w-8 sl:h-8"
                                    src={showPassword ? HidePasswordIcon : ShowPasswordIcon}
                                    alt="Toggle Password Visibility"
                                />
                            </motion.button>
                        </motion.div>

                        <motion.div
                            variants={itemVariants}
                            className="relative w-full"
                        >
                            <input
                                type={showConfirmationPassword ? 'text' : 'password'}
                                value={confirmationPassword}
                                onChange={(e) => setConfirmationPassword(e.target.value)}
                                placeholder="Confirm Password"
                                className="w-full h-input md:h-input-md sl:h-input-lg px-4 md:px-6 sl:px-8 bg-accent rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base md:text-lg sl:text-xl ll:text-2xl placeholder:text-secondary/70 pr-12 md:pr-14 sl:pr-16"
                            />
                            <motion.button
                                type="button"
                                onClick={toggleConfirmationPasswordVisibility}
                                className="absolute right-4 md:right-6 sl:right-8 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-accent-dark"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                <img
                                    className="w-6 h-6 md:w-7 md:h-7 sl:w-8 sl:h-8"
                                    src={showConfirmationPassword ? HidePasswordIcon : ShowPasswordIcon}
                                    alt="Toggle Confirmation Password Visibility"
                                />
                            </motion.button>
                        </motion.div>

                        <motion.button
                            variants={itemVariants}
                            type="button"
                            onClick={handleResetPassword}
                            className="w-full h-input md:h-input-md sl:h-input-lg border-2 border-primary text-primary md:text-lg sl:text-xl ll:text-2xl rounded-lg hover:bg-primary hover:text-white transition-colors font-medium"
                            whileHover="hover"
                            whileTap="tap"
                        >
                            Reset Password
                        </motion.button>
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
    // return (
    //     <div className="flex flex-1 justify-center items-center mx-auto w-full h-screen">
    //         <div className="flex flex-col justify-center items-center mx-auto space-y-8">
    //             <img className="w-[149px] h-[149px]" src={Logo} alt="logo" />

    //             <h1 className="text-[40px] font-[500] text-[#134F98]">
    //                 {!isVerified ? 'We’ve sent you an email' : 'Great!!'}
    //             </h1>
    //             <span className="text-[20px] font-[500] text-[#134F98]">
    //                 {!isVerified
    //                     ? 'Check your inbox for the reset password code.'
    //                     : 'Now you can change your password. Choose it wisely.'}
    //             </span>

    //             {!isVerified ? (
    //                 <div className="flex flex-col justify-center items-center mx-auto space-y-12 w-[493px]">
    //                     <input
    //                         type="text"
    //                         value={token}
    //                         onChange={(e) => setToken(e.target.value)}
    //                         placeholder="Enter Your Code"
    //                         className="input-field h-[70px] bg-[#F3F3F3] w-full"
    //                     />
    //                     <button
    //                         type="button"
    //                         onClick={handleCheckToken}
    //                         className="border-2 border-[#134F98] text-[#134F98] text-[24px] py-2 px-4 rounded-lg w-full h-[69px] hover:bg-[#134F98] hover:text-white transition"
    //                     >
    //                         Verify
    //                     </button>
    //                 </div>
    //             ) : (
    //                 <div className="flex flex-col justify-center items-center mx-auto space-y-12 w-[493px]">
    //                     <div className="relative w-full">
    //                         <input
    //                             type={showPassword ? 'text' : 'password'}
    //                             value={password}
    //                             onChange={(e) => setPassword(e.target.value)}
    //                             placeholder="Password"
    //                             className="input-field h-[70px] bg-[#F3F3F3] w-full"
    //                         />
    //                         <button
    //                             type="button"
    //                             onClick={togglePasswordVisibility}
    //                             className="absolute right-4 top-1/2 transform -translate-y-1/2"
    //                         >
    //                             <img
    //                                 className="w-6 h-6"
    //                                 src={showPassword ? HidePasswordIcon : ShowPasswordIcon}
    //                                 alt="Toggle Password Visibility"
    //                             />
    //                         </button>
    //                     </div>

    //                     <div className="relative w-full">
    //                         <input
    //                             type={showConfirmationPassword ? 'text' : 'password'}
    //                             value={confirmationPassword}
    //                             onChange={(e) => setConfirmationPassword(e.target.value)}
    //                             placeholder="Confirm Password"
    //                             className="input-field h-[70px] bg-[#F3F3F3] w-full"
    //                         />
    //                         <button
    //                             type="button"
    //                             onClick={toggleConfirmationPasswordVisibility}
    //                             className="absolute right-4 top-1/2 transform -translate-y-1/2"
    //                         >
    //                             <img
    //                                 className="w-6 h-6"
    //                                 src={showConfirmationPassword ? HidePasswordIcon : ShowPasswordIcon}
    //                                 alt="Toggle Confirmation Password Visibility"
    //                             />
    //                         </button>
    //                     </div>

    //                     <button
    //                         type="button"
    //                         onClick={handleResetPassword}
    //                         className="border-2 border-[#134F98] text-[#134F98] text-[24px] py-2 px-4 rounded-lg w-full h-[69px] hover:bg-[#134F98] hover:text-white transition"
    //                     >
    //                         Reset Password
    //                     </button>
    //                 </div>
    //             )}
    //         </div>
    //     </div>
    // );
};

export default ResetPassword;
